import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import ExamForm from '../../components/admin/ExamForm';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { getExam, updateExam } from '../../api/admin.api';

// Convert a UTC ISO string to the local datetime-local input format (YYYY-MM-DDTHH:MM)
const toLocalInput = (utcStr) => {
  const d = new Date(utcStr);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Convert a datetime-local value (local time) to a UTC ISO string for the backend
const toUTC = (localStr) => (localStr ? new Date(localStr).toISOString() : '');

const ExamEdit = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getExam(id).then((res) => {
      const d = res.data.data;
      setForm({
        title: d.title,
        description: d.description || '',
        duration_minutes: d.duration_minutes,
        pass_marks: d.pass_marks,
        max_tab_switches: d.max_tab_switches,
        allow_result_view: d.allow_result_view,
        instructions: d.instructions || '',
        scheduled_start: d.scheduled_start ? toLocalInput(d.scheduled_start) : '',
        scheduled_end: d.scheduled_end ? toLocalInput(d.scheduled_end) : '',
        access_code: d.access_code || '',
        negative_marks_per_wrong: d.negative_marks_per_wrong ?? 0,
        shuffle_questions: d.shuffle_questions || false,
      });
    });
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await updateExam(id, {
        ...form,
        scheduled_start: toUTC(form.scheduled_start),
        scheduled_end: toUTC(form.scheduled_end),
      });
      navigate(`/admin/exams/${id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  if (!form) return <AdminLayout><Spinner className="py-20" /></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-2xl">
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-slate-900">Edit Exam</h1>
          <p className="text-slate-500 text-sm mt-0.5">Update exam details and settings.</p>
        </div>
        {error && <Alert type="error" className="mb-5">{error}</Alert>}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-7">
          <ExamForm form={form} setForm={setForm} onSubmit={handleSubmit} loading={loading} submitLabel="Update Exam" />
        </div>
      </div>
    </AdminLayout>
  );
};

export default ExamEdit;
